import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { StatusPill } from "./ui";

const LEAD_STATUSES = ["new", "contacted", "qualified", "proposal", "won", "lost"];

const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await api.delete(`/admin/leads/${id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  return (
    <div data-testid="admin-tab-leads">
      <h2 className="font-display text-xl font-bold tracking-tight">Client Inquiries</h2>
      <p className="mt-1 text-sm text-tark-dim">Service, consultation and project inquiries from the Start a Project form.</p>
      <div className="mt-6 space-y-3">
        {leads.map((lead) => (
          <div key={lead.id} className="rounded-2xl border border-tark-line bg-tark-card" data-testid={`lead-row-${lead.id}`}>
            <button className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 p-5 text-left" onClick={() => setExpanded(expanded === lead.id ? null : lead.id)}>
              <span className="font-display text-sm font-bold text-tark-ink">{lead.name}</span>
              <span className="font-mono text-xs text-tark-mut">{lead.work_email}</span>
              <span className="rounded border border-tark-line px-2 py-0.5 font-mono text-[10px] text-tark-dim">{lead.service_required}</span>
              {lead.company && <span className="text-xs text-tark-dim">{lead.company}</span>}
              <span className="ml-auto flex items-center gap-3">
                <StatusPill status={lead.status} />
                <ChevronDown className={`h-4 w-4 text-tark-dim transition-transform ${expanded === lead.id ? "rotate-180" : ""}`} />
              </span>
            </button>
            {expanded === lead.id && (
              <div className="border-t border-tark-line p-5 text-sm">
                <div className="grid gap-x-8 gap-y-2 font-mono text-xs text-tark-mut sm:grid-cols-2 lg:grid-cols-3">
                  <span>Job Title: {lead.job_title || "—"}</span>
                  <span>Country: {lead.country || "—"}</span>
                  <span>Phone: {lead.phone || "—"}</span>
                  <span>Project Type: {lead.project_type || "—"}</span>
                  <span>Budget: {lead.estimated_budget || "—"}</span>
                  <span>Start: {lead.expected_start_date || "—"}</span>
                </div>
                {lead.project_description && <p className="mt-4 leading-relaxed text-tark-mut">{lead.project_description}</p>}
                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <select value={lead.status} onChange={(e) => setStatus(lead.id, e.target.value)}
                          className="rounded-lg border border-tark-line bg-tark-bg px-3 py-2 font-mono text-xs text-tark-ink"
                          data-testid={`lead-status-${lead.id}`}>
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => remove(lead.id)} className="text-red-400/70 hover:text-red-400" data-testid={`lead-delete-${lead.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {leads.length === 0 && <p className="rounded-2xl border border-tark-line bg-tark-card p-10 text-center font-mono text-xs text-tark-dim">No client inquiries yet.</p>}
      </div>
    </div>
  );
};

export default LeadsTab;
